import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@api';
import { queryKeys } from '@constants';
import { FrameworkKey } from '@libs/constants';
import { GetFrameworkDetailsResponse } from '@libs/types';

/**
 * Fetches framework details by framework key
 * @param frameworkKey - The framework identifier (e.g., 'pdpl')
 * @param version - Optional specific version (defaults to latest)
 * @param signal - AbortSignal for request cancellation
 */
async function getFrameworkDetails(
  frameworkKey: FrameworkKey,
  version?: string,
  signal?: AbortSignal,
): Promise<GetFrameworkDetailsResponse> {
  const queryParams = new URLSearchParams();
  if (version) {
    queryParams.append('version', version);
  }

  const url = `api/frameworks/${frameworkKey}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await apiClient.get<GetFrameworkDetailsResponse>(url, {
    signal,
  });

  return response.data;
}

/**
 * React Query hook to fetch framework details
 * @param frameworkKey - The framework identifier (e.g., 'pdpl')
 * @param version - Optional specific version (defaults to latest)
 */
export const useFrameworkDetails = (frameworkKey: FrameworkKey, version?: string) => {
  return useQuery<GetFrameworkDetailsResponse>({
    queryKey: queryKeys.framework.byKey(frameworkKey, version),
    queryFn: ({ signal }) => getFrameworkDetails(frameworkKey, version, signal),
  });
};
